import { ReactNode } from 'react';

import {
  CustomFieldSelectAttribute,
  CustomFieldStringSetAttributeValue,
} from 'apis/customFields';
import { separateReactNodes, SeparateReactNodesTransformFn } from 'utils/react';
import { convertTreeToFlatItems } from 'utils/tree';

import { CustomField } from '../useLazyCustomFields';

/** Represents the configuration of the function. */
export type GetCustomFieldSelectFieldDisplayValueConfig = {
  /** The leaf class name. */
  readonly leafClassName?: string;
  /** The node class name. */
  readonly nodeClassName?: string;
  /** The separator class name. */
  readonly separatorClassName?: string;
  /** The path separator. */
  readonly pathSeparator?: ReactNode;
  /** The current form value */
  readonly formValue?: string[] | undefined;
};

type GetCustomFieldSelectFieldDisplayValueResult = {
  /** Whether the path is long. */
  readonly isLongPath: boolean;
  /** The unstyled separated full path. */
  readonly rawPath: readonly ReactNode[];
  /** The computed separated styled path. */
  readonly path: readonly ReactNode[];
};

const defaultConfig: GetCustomFieldSelectFieldDisplayValueConfig = {
  pathSeparator: ' / ',
};

/**
 * Gets the display value of a custom select field value.
 *
 * @param customField The custom select field.
 * @param config The configuration object.
 */
export function getCustomSelectFieldDisplayValue(
  customField: CustomField<
    CustomFieldSelectAttribute,
    CustomFieldStringSetAttributeValue
  >,
  config?: GetCustomFieldSelectFieldDisplayValueConfig,
): GetCustomFieldSelectFieldDisplayValueResult | undefined {
  const { attribute, value } = customField;
  const mergedConfig = Object.assign({}, defaultConfig, config);
  const { formValue } = mergedConfig;

  // Display local form value if provided
  const fieldValue = formValue ?? value?.value;
  if (!fieldValue) {
    return;
  }

  const flatOptions = convertTreeToFlatItems(attribute.options, {
    childrenKey: 'options',
  });

  const separator = (
    <span className={mergedConfig.separatorClassName}> / </span>
  );

  const labelPath = fieldValue
    .map((value) => flatOptions.find((option) => option.value === value)?.label)
    .filter(Boolean) as readonly string[];

  const nodeTransformFn: SeparateReactNodesTransformFn = (node, index, nodes) =>
    index === nodes.length - 1 ? (
      <span key={index} className={mergedConfig.leafClassName}>
        {node}
      </span>
    ) : (
      <span key={index} className={mergedConfig.nodeClassName}>
        {node}
      </span>
    );

  const isLongPath = labelPath.length > 3;

  return {
    isLongPath,
    rawPath: separateReactNodes(labelPath, {
      separator: mergedConfig.pathSeparator,
    }),
    path: isLongPath
      ? separateReactNodes(
          [labelPath[0], '…', labelPath[labelPath.length - 1]],
          {
            separator,
            nodeTransformFn,
          },
        )
      : separateReactNodes(labelPath, {
          separator: mergedConfig.pathSeparator,
          nodeTransformFn,
        }),
  };
}
